import { NextSeo } from 'next-seo';
import SEO from 'next-seo.config';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { handleHideSidescreen, uxSelector } from 'store/ux';
import ProfileHeader from 'components/Profile/ProfileHeader';
import AlertNotificationsStatus from 'components/Profile/AlertNotificationsStatus';
import Alert from 'components/popUps/Alert';
import PaymentAlert from 'components/popUps/PaymentAlert';
import SidebarLayout from 'layouts/SidebarLayout';
import { profile } from 'lang/en.json';
import { wrapper } from 'store';
import buildProfileSeo, {
    aboutWithCount,
    coverageClaim,
} from 'utils/profile-seo';
import buildProfileSchema from 'utils/profile-schema';
import { jsonLd } from 'utils/safe-html';
import { notFound, notFoundOrThrow } from 'utils/isr';
import directoryStyles from 'styles/modules/BrokerDirectory.module.scss';

import { companySelector, fetchCompanyBySlug } from 'store/company';
import { coveragesSelector, fetchCoverages } from 'store/company/coverage';
import { fetchStories, storiesSelector } from 'store/company/story';
import { alertSelector, fetchAlerts } from 'store/alert';
import { authSelector, handleAuthorization } from 'store/auth';
import { useRouter } from 'next/router';
import { getReq } from 'api';
import { fetchNews } from 'api/newsroom';
import QuotedIn from 'components/Profile/QuotedIn';
import { fcaRevalidate } from 'utils/fca';
import DirectoryBackLinks from 'components/Directory/DirectoryBackLinks';
import DirectoryBreadcrumb from 'components/Directory/DirectoryBreadcrumb';
import { fetchDirectoryProfile } from 'api/directory';
import { profileBreadcrumbSchema, profileCrumbs } from 'utils/directory';

import dynamic from 'next/dynamic';

const Sidescreen = dynamic(() => import('components/Sidescreen'), {
    ssr: false,
});
const JournalistProfile = dynamic(
    () => import('components/Journalist/Profile'),
    { ssr: false }
);
const CompanyPremiumBanner = dynamic(
    () => import('components/Alert/CompanyPremiumBanner'),
    { ssr: false }
);
// Server-rendered on purpose: this is the Media Coverage section, and it is the
// only substantial indexable text on a profile. The other dynamic imports below
// stay client-only.
const NewspagerProfile = dynamic(() =>
    import('../../components/Newspager/NewspagerProfile')
);
const ContactsBanner = dynamic(() => import('components/ContactsBanner'), {
    ssr: false,
});
const FcaAdminPanel = dynamic(
    () => import('components/Profile/FcaAdminPanel'),
    { ssr: false }
);
const DirectoryAdminPanel = dynamic(
    () => import('components/Profile/DirectoryAdminPanel'),
    { ssr: false }
);
const DeactivateAdminPanel = dynamic(
    () => import('components/Profile/DeactivateAdminPanel'),
    { ssr: false }
);
const DirectoryExcludedBanner = dynamic(
    () =>
        import('components/Profile/DirectoryAdminPanel').then(
            (mod) => mod.DirectoryExcludedBanner
        ),
    { ssr: false }
);

const Index = (props) => {
    const dispatch = useDispatch();
    const [alert, setAlert] = useState(false);
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const { data: company } = useSelector(companySelector);
    const { data: coverages, total: coveragesTotal } =
        useSelector(coveragesSelector);
    const { data: stories } = useSelector(storiesSelector);
    const { data: alerts } = useSelector(alertSelector);
    const { sidescreen } = useSelector(uxSelector);
    const { isAuthenticated, isJournalist, isAdmin } =
        useSelector(authSelector);
    const store = useStore();
    const plan = company?.plan;

    const [showPremiumBanner, setShowPremiumBanner] = useState(false);
    const [directoryStatus, setDirectoryStatus] = useState(null);

    const router = useRouter();

    const isUserJournalist = isJournalist;

    // The page is cached HTML built without a user, so everything that depends
    // on who is looking is resolved here instead of in getStaticProps.
    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }
        dispatch(handleAuthorization(router.query.company));
        if (!store.getState().auth.isAdmin) {
            dispatch(fetchAlerts());
        }
        // The cached page carries no email or phone; a member gets them for Get in touch.
        getReq(`/profiles/slug/${router.query.company}`)
            .then(({ data }) => {
                if (data?.data?.id) {
                    dispatch(fetchCompanyBySlug.fulfilled(data.data));
                }
            })
            .catch(() => {});
    }, [isAuthenticated, router.query.company]);

    useEffect(() => {
        if (router.query.showPremiumBanner != 1) {
            return;
        }
        setShowPremiumBanner(true);
        setTimeout(() => {
            setShowPremiumBanner(false);
        }, 10_000);
    }, []);

    // Check for successful response purchase on page load
    useEffect(() => {
        const purchaseInProgress = localStorage.getItem(
            'responsePurchaseInProgress'
        );
        if (purchaseInProgress === 'true') {
            setShowSuccessAlert(true);
            localStorage.removeItem('responsePurchaseInProgress');
            localStorage.removeItem('responsePurchaseQuantity');
        }
    }, []);

    const showJournalistProfile =
        company.name === '' ||
        (company.publication === '' && company.isAuthenticated);

    return (
        <>
            <NextSeo
                {...SEO}
                {...props.seo}
                titleTemplate="%s"
                openGraph={{ ...SEO.openGraph, ...props.seo?.openGraph }}
            />
            {props.schema && (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: jsonLd(props.schema),
                    }}
                />
            )}
            {props.directoryCrumbs && (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: jsonLd(
                            profileBreadcrumbSchema(
                                props.directoryCrumbs,
                                process.env.HOST
                            )
                        ),
                    }}
                />
            )}
            {showPremiumBanner && (
                <CompanyPremiumBanner
                    {...{ setShowPremiumBanner }}
                    plan={plan}
                />
            )}
            <SidebarLayout isAuthenticated={isAuthenticated}>
                {isUserJournalist && showJournalistProfile && (
                    <JournalistProfile
                        name={company.name}
                        publication={company.publication}
                        website={company.website}
                        addresses={company.addresses}
                    />
                )}
                <Alert
                    isActive={alert}
                    alertMessage={alert}
                    onCancel={() => {
                        setAlert(false);
                    }}
                />
                {!isUserJournalist && <AlertNotificationsStatus />}
                {props.directoryCrumbs && (
                    <div className={directoryStyles.profileBreadcrumb}>
                        <DirectoryBreadcrumb items={props.directoryCrumbs} />
                    </div>
                )}
                {isAdmin === true && directoryStatus?.excluded && (
                    <DirectoryExcludedBanner />
                )}
                <ProfileHeader
                    data={company}
                    coverageClaim={props.coverageClaim}
                    aboutWithCount={props.aboutWithCount}
                />
                {isAdmin === true && company.id && !company.isJournalist && (
                    <>
                        <FcaAdminPanel profile={company} />
                        <DirectoryAdminPanel
                            profile={company}
                            onStatus={setDirectoryStatus}
                        />
                        <DeactivateAdminPanel profile={company} />
                    </>
                )}
                {!isUserJournalist && (
                    <>
                        <NewspagerProfile
                            {...{
                                company,
                                coverages,
                                coveragesTotal,
                                stories,
                                alerts,
                                news_stories: profile.news_stories,
                            }}
                        />
                        <QuotedIn
                            {...props.quotedIn}
                            companySlug={company.slug}
                        />
                        <DirectoryBackLinks directory={props.directory} />
                        <ContactsBanner title={profile.contact.title} />
                    </>
                )}

                {sidescreen?.active && (
                    <Sidescreen
                        onUpdate={(val) => {
                            dispatch(handleHideSidescreen());
                            setAlert(val);
                        }}
                    />
                )}
            </SidebarLayout>

            {showSuccessAlert && (
                <PaymentAlert
                    isActive={true}
                    alertTitle="Payment Successful!"
                    alertMessage="Thank you for your purchase. Your responses have been added to your account."
                    onCancel={() => {
                        setShowSuccessAlert(false);
                    }}
                />
            )}
        </>
    );
};

const QUOTED_IN_SIZE = 10;
const DIRECTORY_TIMEOUT_MS = 3000;

export const getStaticPaths = () => ({ paths: [], fallback: 'blocking' });

export const getStaticProps = wrapper.getStaticProps(
    (store) =>
        async ({ params }) => {
            try {
                await store.dispatch(fetchCompanyBySlug(params.company));
                await Promise.all(
                    [fetchStories(), fetchCoverages()].map((action) =>
                        store.dispatch(action)
                    )
                );
            } catch (err) {
                return notFoundOrThrow(err);
            }

            const companyState = store.getState().company;

            if (!companyState.data?.id || companyState.data.deactivated) {
                return notFound();
            }

            const coverage = {
                total: companyState.coverages.total,
                items: companyState.coverages.data,
            };

            const stories = companyState.stories.data.filter(
                (item) => item.status === 'publish'
            );

            // The directory links are optional: a slow or failing directory
            // API must not delay or take down the profile.
            const [quotedIn, directory] = await Promise.all([
                fetchNews({
                    profile: companyState.data.slug,
                    size: QUOTED_IN_SIZE,
                }),
                fetchDirectoryProfile(companyState.data.slug, {
                    timeout: DIRECTORY_TIMEOUT_MS,
                }).catch(() => null),
            ]);
            const listed = directory?.listed ? directory : null;

            return {
                props: {
                    data: companyState.data,
                    stories,
                    coverages: companyState.coverages.data,
                    quotedIn: {
                        items: (quotedIn?.data ?? []).map(
                            ({ slug, headline, publishedAt }) => ({
                                slug,
                                headline,
                                publishedAt,
                            })
                        ),
                        total: quotedIn?.totalItems ?? 0,
                    },
                    // Computed here, not in the component: coverageClaim reads
                    // Date.now(), so rendering it client-side could disagree
                    // with the server across a day boundary and break
                    // hydration. This also puts it in the server HTML, which
                    // is the whole point of showing it.
                    coverageClaim: coverageClaim(coverage),
                    // `from` lets the page drop it once the member edits the About text.
                    aboutWithCount: {
                        from: companyState.data.description ?? null,
                        html: aboutWithCount(coverage, companyState.data),
                    },
                    directory: listed,
                    directoryCrumbs: profileCrumbs(companyState.data, listed),
                    seo: buildProfileSeo(
                        companyState.data,
                        process.env.HOST,
                        coverage
                    ),
                    schema: buildProfileSchema(
                        companyState.data,
                        process.env.HOST,
                        coverage
                    ),
                },
                revalidate: fcaRevalidate(companyState.data.fca),
            };
        }
);

// No wrapper.withRedux here — _app.js already wraps the tree. Double-wrapping
// gives this page a second store that never receives HYDRATE, so everything
// rendered from useSelector comes out empty server-side.
export default Index;
